<template>
  <div class="container mt-4">
    <div class="card p-4 shadow border-0" style="border-radius: 16px;">
      <h4 class="mb-4" style="color: #b03a45;">
        <i class="fas fa-project-diagram me-2"></i> {{ $t('project.title') }}
      </h4>

      <!-- Form -->
      <form @submit.prevent="handleSubmit">
        <div class="row">
          <div class="col-md-6 mb-4">
            <div class="p-3 bg-white shadow-sm rounded">
              <label class="form-label fw-bold text-dark">{{ $t('project.projectCode') }}</label>
              <input type="text" class="form-control" v-model="form.code" required />
            </div>
          </div>

          <div class="col-md-6 mb-4">
            <div class="p-3 bg-white shadow-sm rounded">
              <label class="form-label fw-bold text-dark">{{ $t('project.projectName') }}</label>
              <input type="text" class="form-control" v-model="form.name" required />
            </div>
          </div>

          <div class="col-md-6 mb-4">
            <div class="p-3 bg-white shadow-sm rounded">
              <label class="form-label fw-bold text-dark">{{ $t('project.type') }}</label>
              <select class="form-select" v-model="form.type">
                <option value="Temporary">{{ $t('project.temporary') }}</option>
                <option value="Permanent">{{ $t('project.permanent') }}</option>
              </select>
            </div>
          </div>

          <div class="col-md-6 mb-4">
            <div class="p-3 bg-white shadow-sm rounded">
              <label class="form-label fw-bold text-dark">{{ $t('project.parentProject') }}</label>
              <select class="form-select" v-model="form.parent_id">
                <option :value="null">{{ $t('project.none') }}</option>
                <option v-for="parent in parentProjects" :key="parent.id" :value="parent.id">
                  {{ parent.name }}
                </option>
              </select>
            </div>
          </div>

          <div class="col-md-6 mb-4">
            <div class="p-3 bg-white shadow-sm rounded">
              <label class="form-label fw-bold text-dark">{{ $t('project.startDate') }}</label>
              <input type="date" class="form-control" v-model="form.start_date" />
            </div>
          </div>

          <div class="col-md-6 mb-4">
            <div class="p-3 bg-white shadow-sm rounded">
              <label class="form-label fw-bold text-dark">{{ $t('project.endDate') }}</label>
              <input type="date" class="form-control" v-model="form.end_date" />
            </div>
          </div>

          <div class="col-md-6 mb-4">
            <div class="p-3 bg-white shadow-sm rounded">
              <label class="form-label fw-bold text-dark">{{ $t('project.contactPhone') }}</label>
              <input type="text" class="form-control" v-model="form.contact_phone" />
            </div>
          </div>

          <div class="col-md-6 mb-4">
            <div class="p-3 bg-white shadow-sm rounded">
              <label class="form-label fw-bold text-dark">{{ $t('project.contactEmail') }}</label>
              <input type="email" class="form-control" v-model="form.contact_email" />
            </div>
          </div>

          <div class="col-12 mb-4">
            <div class="p-3 bg-white shadow-sm rounded">
              <label class="form-label fw-bold text-dark">{{ $t('project.description') }}</label>
              <textarea class="form-control" rows="3" v-model="form.description"></textarea>
            </div>
          </div>

          <div class="col-md-6 mb-3 d-flex align-items-center justify-content-between">
            <label class="form-label fw-bold text-dark mb-0">{{ $t('project.status') }}</label>
            <div class="form-check form-switch">
              <input class="form-check-input" type="checkbox" v-model="form.is_active" />
              <label class="form-check-label">
                {{ form.is_active ? $t('project.active') : $t('project.inactive') }}
              </label>
            </div>
          </div>
        </div>

        <div class="d-flex gap-3 mt-3">
          <button type="submit" class="btn text-white px-4" style="background-color: #b03a45;">
            {{ editingId ? $t('project.update') : $t('project.add') }}
          </button>
          <button type="button" class="btn btn-secondary px-4" @click="resetForm">{{ $t('project.cancel') }}</button>
        </div>
      </form>

      <!-- List -->
      <hr class="my-4" />
      <h5 class="mb-3" style="color: #b03a45;">
        <i class="fas fa-list me-2"></i> {{ $t('project.existingProjects') }}
      </h5>

      <div v-if="projects.length" class="table-responsive">
        <table class="table table-bordered align-middle">
          <thead class="table-light">
            <tr>
              <th>{{ $t('project.code') }}</th>
              <th>{{ $t('project.name') }}</th>
              <th>{{ $t('project.type') }}</th>
              <th>{{ $t('project.parent') }}</th>
              <th>{{ $t('project.status') }}</th>
              <th class="text-center">{{ $t('project.actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="project in projects" :key="project.id">
              <td>{{ project.code }}</td>
              <td>{{ project.name }}</td>
              <td>{{ project.type }}</td>
              <td>{{ project.parent?.name || '-' }}</td>
              <td>
                <span :class="project.is_active ? 'text-success' : 'text-danger'">
                  {{ project.is_active ? $t('project.active') : $t('project.inactive') }}
                </span>
              </td>
              <td class="text-center">
                <button class="btn btn-outline-danger btn-sm me-2" @click="loadProject(project)">{{ $t('project.edit') }}</button>
                <button class="btn btn-outline-secondary btn-sm me-2" @click="deleteProject(project.id)">{{ $t('project.delete') }}</button>
                <button class="btn btn-outline-dark btn-sm" @click="toggleProject(project.id)">{{ $t('project.toggle') }}</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p v-else class="text-muted">{{ $t('project.noProjectsFound') }}</p>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import projectService from '@/services/projectService'
import { useToast } from 'vue-toastification'

const toast = useToast()

const projects = ref([])
const parentProjects = ref([])
const editingId = ref(null)

const form = ref({
  code: '',
  name: '',
  type: 'Temporary',
  parent_id: null,
  start_date: '',
  end_date: '',
  description: '',
  contact_phone: '',
  contact_email: '',
  is_active: true
})

const fetchProjects = async () => {
  const { data } = await projectService.getAll()
  projects.value = data || []
  parentProjects.value = data || []
}

const handleSubmit = async () => {
  try {
    if (editingId.value) {
      await projectService.update(editingId.value, form.value)
      toast.success($t('project.projectUpdated'))
    } else {
      await projectService.create(form.value)
      toast.success($t('project.projectAdded'))
    }
    resetForm()
    fetchProjects()
  } catch (err) {
    toast.error($t('project.errorSavingProject'))
    console.error(err)
  }
}

const loadProject = (project) => {
  editingId.value = project.id
  form.value = {
    code: project.code,
    name: project.name,
    type: project.type,
    parent_id: project.parent_id ?? null,
    start_date: project.start_date,
    end_date: project.end_date,
    description: project.description,
    contact_phone: project.contact_phone,
    contact_email: project.contact_email,
    is_active: project.is_active
  }
}

const resetForm = () => {
  editingId.value = null
  form.value = {
    code: '',
    name: '',
    type: 'Temporary',
    parent_id: null,
    start_date: '',
    end_date: '',
    description: '',
    contact_phone: '',
    contact_email: '',
    is_active: true
  }
}

const deleteProject = async (id) => {
  try {
    await projectService.delete(id)
    toast.success($t('project.projectDeleted'))
    fetchProjects()
  } catch (err) {
    toast.error($t('project.errorDeletingProject'))
    console.error(err)
  }
}

const toggleProject = async (id) => {
  try {
    await projectService.deactivate(id)
    toast.success($t('project.statusChanged'))
    fetchProjects()
  } catch (err) {
    toast.error($t('project.toggleFailed'))
    console.error(err)
  }
}

onMounted(() => {
  fetchProjects()
})
</script>